import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const buildErrors = (t, values, existingUuids) => {
  const e = {};
  const name = String(values.name ?? "").trim();
  const uuid = String(values.uuid ?? "").trim();
  if (!name) {
    e.name = t("pages.pos.workspaces.validation.nameRequired");
  }
  if (!uuid) {
    e.uuid = t("pages.pos.workspaces.validation.uuidRequired");
  } else {
    const dup = (existingUuids || []).some((u) => String(u).trim() === uuid);
    if (dup) {
      e.uuid = t("pages.pos.workspaces.validation.duplicateUuid");
    }
  }
  return e;
};

/**
 * Create/edit a single workspace row. Keeps other fields (features, pos, devices) on the object for future extension.
 */
const PosWorkspaceFormModal = ({
  open,
  mode,
  workspace,
  existingUuids,
  onClose,
  onSave,
  saveInProgress = false,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ name: "", uuid: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const title = useMemo(
    () =>
      mode === "edit"
        ? t("pages.pos.workspaces.modalEditTitle")
        : t("pages.pos.workspaces.modalCreateTitle"),
    [mode, t],
  );

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    if (mode === "edit" && workspace && typeof workspace === "object") {
      setValues({
        name: workspace.name != null ? String(workspace.name) : "",
        uuid: workspace.uuid != null ? String(workspace.uuid) : "",
      });
    } else {
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `ws-${Date.now()}`;
      setValues({ name: "", uuid });
    }
  }, [open, mode, workspace]);

  const handleSubmit = async () => {
    const errs = buildErrors(t, values, existingUuids);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    const base =
      mode === "edit" && workspace && typeof workspace === "object"
        ? { ...workspace }
        : { features: {}, pos: {}, devices: [] };

    try {
      await Promise.resolve(
        onSave?.({
          ...base,
          name: String(values.name).trim(),
          uuid: String(values.uuid).trim(),
        }),
      );
    } catch (_e) {
      /* parent shows API errors */
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saveInProgress ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            required
            fullWidth
            label={t("pages.pos.workspaces.fieldName")}
            value={values.name}
            onChange={(e) => {
              setValues((v) => ({ ...v, name: e.target.value }));
              setFieldErrors((er) => ({ ...er, name: undefined }));
            }}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || ""}
          />
          <TextField
            required
            fullWidth
            label={t("pages.pos.workspaces.fieldUuid")}
            value={values.uuid}
            onChange={
              mode === "edit"
                ? (e) => {
                    setValues((v) => ({ ...v, uuid: e.target.value }));
                    setFieldErrors((er) => ({ ...er, uuid: undefined }));
                  }
                : undefined
            }
            inputProps={{
              readOnly: mode === "create",
            }}
            error={Boolean(fieldErrors.uuid)}
            helperText={fieldErrors.uuid || ""}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("pages.pos.workspaces.advancedFieldsHint")}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveInProgress}>
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={saveInProgress}
        >
          {saveInProgress ? t("pages.pos.workspaces.saving") : t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PosWorkspaceFormModal;
